// (C) Copyright 2025 Hewlett Packard Enterprise Development LP
import React, { useCallback, useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Box } from 'grommet'
import { useNavigate, useParams } from 'react-router-dom'
import lodashGet from 'lodash/get'
import { StatusCritical } from 'grommet-icons'
import { useFlags } from 'launchdarkly-react-client-sdk'
/* eslint-disable import/no-unresolved */
import { useReactOidc } from '@axa-fr/react-oidc-context'

import { isMSP, isV2 } from '../../../../../utils/feature-flag-utils'
import {
  Notification,
  Anchor,
  Loader,
  Typography,
  Wizard
} from '../../../../../components'
import { validateForm } from '../../../../../utils/validation-utils'
import {
  getErrorMessage,
  get,
  post,
  del,
  put
} from '../../../../../utils/api-utils'
import { displayApiError } from '../../../../../utils/error-handling-utils'
import {
  Authnoptions,
  dispositionEnum,
  mspOptions,
  oidcauthorization,
  ssoModeEnum
} from '../../constants'

import General from './steps/General'
import {
  validateConfigureSettingsFields,
  validateGeneralFields,
  validateIDPDetails,
  validateRecoveryUser,
  validateTimeoutField,
  validateOIDCMetadata,
  validateClientFields
} from './steps/utils/validation-utils'
import ConfigureSettings from './steps/ConfigureSettings'
import ConfigureIdp from './steps/ConfigureIdp'
import AddIDPDetails from './steps/add-idp-details/AddIDPDetails'
import SamlOIDCConfiguration from './steps/SamlOIDCConfiguration'
import ReviewAndCreate from './steps/ReviewAndCreate'
import { pullSSOConfigStatus } from './steps/utils/api-utils'
import Timeout from './steps/Timeout'
import {
  attributeMappingDefaultFormValues,
  defaultGeneral
} from './steps/utils/page-utils'
import RecoveryAccount from './steps/recovery-account/RecoveryAccount'
import LoginClientSetUp from './steps/add-idp-details/components/LoginClient'

const AddSsoProfile = () => {
  const navigate = useNavigate()
  const { oidcUser } = useReactOidc()
  const { t } = useTranslation(['common', 'iam', 'authn'])
  const { id } = useParams()
  const [configValues] = useState(null)
  const [multiDomainErrorInfo, setMultiDomainErrorInfo] = useState({})
  const [disableFinishOnSubmit, setDisableFinishOnSubmit] = useState(true)
  const [errorMsg, setErrorMsg] = useState(null)
  const [profileLoading, setProfileLoading] = useState(false)
  const [routingLoading, setRoutingLoading] = useState(false)
  const [apiErrorNotification, setApiErrorNotification] = useState(null)
  const [profileData, setProfileData] = useState(null)
  const [routingData, setRoutingData] = useState(null)
  const [createdSsoProfileData, setCreatedSsoProfileData] = useState(null)
  const [updatedSsoProfileData, setUpdatedSsoProfileData] = useState(null)
  const [initialFormData, setInitialFormData] = useState({})
  const LDFlags = useFlags()
  const isGlcpOidc = LDFlags['glcp-oidc']
  const [selectedOption, setSelectedOption] = useState(null)
  const [issuerUrl, setIssuerUrl] = useState(null)
  const isDomainSelected = (formValues) => {
    const isvalid =
      lodashGet(formValues, 'domain') &&
      lodashGet(formValues, 'domain') !== 'none'
    return isvalid
  }

  const handleRoutingRuleCreateErrorAndNavigate = (profileId) => {
    if (profileId) {
      navigate(`/manage-account/organization/sso-profiles/${profileId}`, {
        notificationError: t(
          'iam:sso_profiles.view_edit_create.domain_assignment_error'
        )
      })
    } else {
      navigate(`/manage-account/organization/sso-profiles`, {
        state: {
          notificationError: t(
            'iam:sso_profiles.view_edit_create.domain_assignment_error'
          )
        }
      })
    }
  }

  const actionOnExitFn = useCallback(() => {
    if (id) {
      if (updatedSsoProfileData) {
        const ssoProfileName = updatedSsoProfileData?.name
        navigate(
          `/manage-account/organization/sso-profiles/${updatedSsoProfileData.id}`,
          {
            notification: t(
              'iam:sso_profiles.view_edit_create.profile_updated',
              {
                ssoProfileName
              }
            )
          }
        )
      } else {
        navigate(`/manage-account/organization/sso-profiles/${id}`)
      }
    } else if (createdSsoProfileData) {
      const ssoProfileName = createdSsoProfileData?.name
      navigate(`/manage-account/organization/sso-profiles`, {
        state: {
          notification: t('iam:sso_profiles.view_edit_create.profile_created', {
            ssoProfileName
          })
        }
      })
    } else {
      navigate('/manage-account/organization/sso-profiles')
    }
  }, [createdSsoProfileData, navigate, id, updatedSsoProfileData, t])

  useEffect(() => {
    if (createdSsoProfileData || updatedSsoProfileData) {
      actionOnExitFn()
    }
  }, [createdSsoProfileData, updatedSsoProfileData, actionOnExitFn])

  useEffect(() => {
    if (profileData?.oidcIdpConfig && selectedOption !== 'OIDC') {
      setSelectedOption('OIDC')
    }
  }, [profileData, selectedOption])

  const ssoProfileRequestBodyOIDC = (formValues) => {
    const body = {
      name: lodashGet(formValues, 'general.profileName', ''),
      oidcIdpConfig: {
        issuer: lodashGet(formValues, 'oidcIdpConfig.issuer', ''),
        authorizationEndpoint: lodashGet(
          formValues,
          'oidcIdpConfig.authorizationEndpoint',
          ''
        ),
        tokenEndpoint: lodashGet(formValues, 'oidcIdpConfig.tokenEndpoint', ''),
        jwksEndpoint: lodashGet(formValues, 'oidcIdpConfig.jwksEndpoint', ''),
        userInfoEndpoint: lodashGet(
          formValues,
          'oidcIdpConfig.userInfoEndpoint',
          ''
        ),
        clientId: lodashGet(formValues, 'oidcIdpConfig.clientId', ''),
        clientSecret: lodashGet(formValues, 'oidcIdpConfig.clientSecret', ''),
        usePkce: lodashGet(formValues, 'oidcIdpConfig.usePkce', true),
        sessionTimeout: 15
      }
    }
    return body
  }

  const ssoProfileRequestBody = (formValues) => {
    const attributeMapping = [
      {
        name: 'Email',
        expression: lodashGet(formValues, 'attribute_mapping.email').toString()
      },
      {
        name: 'FirstName',
        expression: lodashGet(
          formValues,
          'attribute_mapping.firstName'
        ).toString()
      },
      {
        name: 'LastName',
        expression: lodashGet(
          formValues,
          'attribute_mapping.lastName'
        ).toString()
      },
      {
        name: 'IdleSessionTimeout',
        expression: lodashGet(formValues, 'timeout').toString()
      }
      // Uncomment the below code if CountryCode is needed
      // {
      //   name: "CountryCode",
      //   expression: lodashGet(formValues, 'attribute_mapping.countryCode').toString()
      // },
    ]

    // Check if ccsAttribute has a value and add it to the array if it does
    const ccsAttribute = lodashGet(
      formValues,
      'attribute_mapping.ccsAttribute'
    ).toString()
    if (
      lodashGet(formValues, 'general.authorizationMethod') ===
        ssoModeEnum.AUTHORIZATION ||
      lodashGet(formValues, 'general.oidauthorizationMethod') ===
        oidcauthorization.SSO
    ) {
      attributeMapping.push({
        name: 'HPECCSAttribute',
        expression: ccsAttribute
      })
    }
    const Authorization = lodashGet(formValues, 'general.Authnoptions')
    const OIDCbody = {
      name: lodashGet(formValues, 'general.profileName').toString(),
      oidcIdpConfig: {
        clientId: lodashGet(formValues, 'oidcIdpConfig.clientId'),
        clientSecret: lodashGet(formValues, 'oidcIdpConfig.clientSecret'),
        usePkce: lodashGet(formValues, 'oidcIdpConfig.usePkce'),
        issuer: lodashGet(formValues, 'oidcIdpConfig.issuer'),
        authorizationEndpoint: lodashGet(
          formValues,
          'oidcIdpConfig.authorizationEndpoint'
        ),
        tokenEndpoint: lodashGet(formValues, 'oidcIdpConfig.tokenEndpoint'),
        jwksEndpoint: lodashGet(formValues, 'oidcIdpConfig.jwksEndpoint'),
        userInfoEndpoint: lodashGet(
          formValues,
          'oidcIdpConfig.userInfoEndpoint'
        ),
        sessionTimeout: lodashGet(formValues, 'timeout')
      }
    }

    const body = {
      name: lodashGet(formValues, 'general.profileName').toString(),
      attributeMapping,
      samlIdpConfig: {
        entityId: lodashGet(formValues, 'saml_idp_config.entity_id'),
        loginUrl: lodashGet(formValues, 'saml_idp_config.login_url'),
        logoutUrl: lodashGet(formValues, 'saml_idp_config.logout_url'),
        certificate: lodashGet(
          formValues,
          'saml_idp_config.signing_certificate'
        )
      }
    }

    if (lodashGet(formValues, 'recovery_user.pocEmail')) {
      if (lodashGet(formValues, 'recovery_user.recoveryPassword')) {
        body.recoveryUser = {
          username: lodashGet(formValues, 'recovery_user.recoveryEmail'),
          password: lodashGet(formValues, 'recovery_user.recoveryPassword'),
          recoveryEmail: lodashGet(formValues, 'recovery_user.pocEmail')
        }
        OIDCbody.recoveryUser = {
          username: lodashGet(formValues, 'recovery_user.recoveryEmail'),
          password: lodashGet(formValues, 'recovery_user.recoveryPassword'),
          recoveryEmail: lodashGet(formValues, 'recovery_user.pocEmail')
        }
      } else {
        body.recoveryUser = {
          username: lodashGet(formValues, 'recovery_user.recoveryEmail'),
          recoveryEmail: lodashGet(formValues, 'recovery_user.pocEmail')
        }
        OIDCbody.recoveryUser = {
          username: lodashGet(formValues, 'recovery_user.recoveryEmail'),
          recoveryEmail: lodashGet(formValues, 'recovery_user.pocEmail')
        }
      }
    }
    if (isGlcpOidc && Authorization === 'OIDC') {
      return OIDCbody
    }
    return body
  }
  const ssoModeMspValue = (auth_value, msp_value) => {
    const msp_org = msp_value.includes(mspOptions.mspOrg)
    const customer_org = msp_value.includes(mspOptions.custOrg)
    const sso = auth_value === ssoModeEnum.AUTHORIZATION
    const local = auth_value === ssoModeEnum.AUTHENTICATION_ONLY

    let ssoModeText = ''
    switch (true) {
      case msp_org && customer_org && sso:
        ssoModeText = ssoModeEnum.AUTHORIZATION_MSP_MANAGED
        return ssoModeText
      case msp_org && customer_org && local:
        ssoModeText = ssoModeEnum.AUTHENTICATION_ONLY_MSP_MANAGED
        return ssoModeText
      case msp_org && sso:
        ssoModeText = ssoModeEnum.AUTHORIZATION
        return ssoModeText
      case customer_org && sso:
        ssoModeText = ssoModeEnum.AUTHORIZATION_MSP_MANAGED_TENANT_ONLY
        return ssoModeText
      case msp_org && local:
        ssoModeText = ssoModeEnum.AUTHENTICATION_ONLY
        return ssoModeText
      case customer_org && local:
        ssoModeText = ssoModeEnum.AUTHENTICATION_ONLY_MSP_MANAGED_TENANT_ONLY
        return ssoModeText
      default:
        break
    }
    return ssoModeText
  }

  const ssomodeLD = (auth_value) => {
    const sso = auth_value === oidcauthorization.SSO
    const local = auth_value === oidcauthorization.LOCAL
    let text = ''
    switch (true) {
      case sso:
        text = ssoModeEnum.AUTHORIZATION
        return text
      case local:
        text = ssoModeEnum.AUTHENTICATION_ONLY
        return text
      default:
        break
    }
    return text
  }
  const ssoModeMspLdValue = (auth_value, msp_value) => {
    const msp_org = msp_value.includes(mspOptions.mspOrg)
    const customer_org = msp_value.includes(mspOptions.custOrg)
    const sso = auth_value === oidcauthorization.SSO
    const local = auth_value === oidcauthorization.LOCAL
    let text = ''
    switch (true) {
      case msp_org && customer_org && sso:
        text = ssoModeEnum.AUTHORIZATION_MSP_MANAGED
        return text
      case msp_org && customer_org && local:
        text = ssoModeEnum.AUTHENTICATION_ONLY_MSP_MANAGED
        return text
      case msp_org && sso:
        text = ssoModeEnum.AUTHORIZATION
        return text
      case customer_org && sso:
        text = ssoModeEnum.AUTHORIZATION_MSP_MANAGED_TENANT_ONLY
        return text
      case msp_org && local:
        text = ssoModeEnum.AUTHENTICATION_ONLY
        return text
      case customer_org && local:
        text = ssoModeEnum.AUTHENTICATION_ONLY_MSP_MANAGED_TENANT_ONLY
        return text
      default:
        break
    }
    return text
  }
  const routingRuleRequestBody = (formValues, ssoProfileId) => {
    if (isGlcpOidc) {
      return {
        conditions: {
          domains: [lodashGet(formValues, 'domain')]
        },
        actions: {
          idp: {
            ssoProfileId,
            ...(isMSP()
              ? {
                  ssoMode: ssoModeMspLdValue(
                    lodashGet(formValues, 'general.oidauthorizationMethod'),
                    lodashGet(formValues, 'msp_methods')
                  )
                }
              : {
                  ssoMode: ssomodeLD(
                    lodashGet(formValues, 'general.oidauthorizationMethod')
                  )
                })
          }
        }
      }
    }
    return {
      conditions: {
        domains: [lodashGet(formValues, 'domain')]
      },
      actions: {
        idp: {
          ssoProfileId,
          ...(isMSP() && isV2()
            ? {
                ssoMode: ssoModeMspValue(
                  lodashGet(formValues, 'general.authorizationMethod'),
                  lodashGet(formValues, 'msp_methods')
                )
              }
            : { ssoMode: lodashGet(formValues, 'general.authorizationMethod') })
        }
      }
    }
  }

  const createRoutingRule = async (
    formValues,
    i18nTranslate,
    newProfileData
  ) => {
    if (isDomainSelected(formValues)) {
      const routingRuleRequest = routingRuleRequestBody(
        formValues,
        newProfileData.id
      )

      try {
        const result = await post(
          '/internal-identity/v1alpha1/sso-routing-rules',
          routingRuleRequest
        )

        pullSSOConfigStatus(
          result.headers.get('location'),
          oidcUser,
          () => {
            setErrorMsg('')
            if (id) {
              setUpdatedSsoProfileData(newProfileData)
            } else {
              setCreatedSsoProfileData(newProfileData)
            }
          },
          () => {
            handleRoutingRuleCreateErrorAndNavigate(newProfileData.id)
          }
        )
      } catch (error) {
        handleRoutingRuleCreateErrorAndNavigate(newProfileData.id)
      }
    } else {
      setErrorMsg('')
      if (id) {
        setUpdatedSsoProfileData(newProfileData)
      } else {
        setCreatedSsoProfileData(newProfileData)
      }
    }
  }

  const validateOidcClientId = async (
    formValues,
    setFormError,
    i18nTranslate
  ) => {
    try {
      const ssoProfileRequest = ssoProfileRequestBodyOIDC(formValues)

      const response = id
        ? await put(
            `/identity/v1alpha1/sso-profiles/${id}?dry-run=true`,
            ssoProfileRequest
          )
        : await post(
            '/identity/v1alpha1/sso-profiles?dry-run=true',
            ssoProfileRequest
          )

      if ([200, 201, 202].includes(response?.status)) {
        return true // Indicate success
      }
      setFormError('Unexpected response status')
      return false

      // await createRoutingRule(formValues, i18nTranslate, response.data)
    } catch (error) {
      const backendErrorMessage = getErrorMessage(error, i18nTranslate)
      setFormError(backendErrorMessage)
      setDisableFinishOnSubmit(false)
      return false
    }
  }

  const createSSOProfile = async (formValues, setFormError, i18nTranslate) => {
    try {
      const ssoProfileRequest = ssoProfileRequestBody(formValues)

      const response = await post(
        '/identity/v1alpha1/sso-profiles',
        ssoProfileRequest
      )

      await createRoutingRule(formValues, i18nTranslate, response.data)
    } catch (error) {
      const backendErrorMessage = getErrorMessage(error, i18nTranslate)
      setFormError(backendErrorMessage)
      setDisableFinishOnSubmit(false)
    }
  }

  const removeAttribute = (obj, attribute) => {
    const { [attribute]: removed, ...rest } = obj
    return rest
  }

  const hasFormChanged = (formData, ignoreAttribute = '') => {
    const filteredFormData = removeAttribute(formData, ignoreAttribute)
    const filteredInitialFormData = removeAttribute(
      initialFormData,
      ignoreAttribute
    )

    return (
      JSON.stringify(filteredFormData) !==
      JSON.stringify(filteredInitialFormData)
    )
  }

  const updateSsoProfile = async (formValues, setFormError, i18nTranslate) => {
    try {
      if (!hasFormChanged(formValues)) {
        setFormError(t('iam:sso_profiles.view_edit_create.no_changes_error'))
        setDisableFinishOnSubmit(false)
        return
      }

      const ssoProfileRequest = ssoProfileRequestBody(formValues)

      const authorizationMethod = lodashGet(
        formValues,
        'general.authorizationMethod'
      )

      const oidauthorizationMethod = lodashGet(
        formValues,
        'general.oidauthorizationMethod'
      )

      const currentAuthorizationMethod = lodashGet(
        formValues,
        'general.currentAuthorizationMethod'
      )

      const currentOidcAuthorizationMethod = lodashGet(
        formValues,
        'general.currentOidcAuthorizationMethod'
      )

      if (hasFormChanged(formValues, 'domain')) {
        await put(
          `/identity/v1alpha1/sso-profiles/${id}?dry-run=${true}`,
          ssoProfileRequest
        )
      }

      if (routingData) {
        try {
          let deleteUsers = false
          if (
            authorizationMethod !== currentAuthorizationMethod ||
            oidauthorizationMethod !== currentOidcAuthorizationMethod
          ) {
            if (
              authorizationMethod === ssoModeEnum.AUTHORIZATION ||
              oidauthorizationMethod === oidcauthorization.SSO ||
              ((authorizationMethod === ssoModeEnum.AUTHENTICATION_ONLY ||
                oidauthorizationMethod === oidcauthorization.LOCAL) &&
                lodashGet(formValues, 'general.usersDisposition') ===
                  dispositionEnum.MOST_SECURE)
            ) {
              deleteUsers = true
            }
          }

          const url = `/internal-identity/v1alpha1/sso-routing-rules/${routingData.id}?remove-users=${deleteUsers}`
          const response = await del(url, {})
          if (response.status === 202) {
            await pullSSOConfigStatus(
              response.headers.get('location'),
              oidcUser,
              async () => {
                let ssoUpdateResponse = null
                if (hasFormChanged(formValues, 'domain')) {
                  ssoUpdateResponse = await put(
                    `/identity/v1alpha1/sso-profiles/${id}`,
                    ssoProfileRequest
                  )
                }

                await createRoutingRule(
                  formValues,
                  i18nTranslate,
                  ssoUpdateResponse?.data || profileData
                )
              },
              (error) => {
                const backendErrorMessage = getErrorMessage(
                  error,
                  i18nTranslate
                )
                setFormError(backendErrorMessage)
                setDisableFinishOnSubmit(false)
              }
            )
          } else {
            setFormError(
              t('iam:sso_profiles.view_edit_create.remove_domain_error', {
                domain: routingData.conditions.domains[0]
              })
            )
            setDisableFinishOnSubmit(false)
          }
        } catch (error) {
          setFormError(
            t('iam:sso_profiles.view_edit_create.remove_domain_error', {
              domain: routingData.conditions.domains[0]
            })
          )
          setDisableFinishOnSubmit(false)
        }
      } else {
        let response = null
        if (hasFormChanged(formValues, 'domain')) {
          response = await put(
            `/identity/v1alpha1/sso-profiles/${id}`,
            ssoProfileRequest
          )
        }

        await createRoutingRule(
          formValues,
          i18nTranslate,
          response?.data || profileData
        )
      }
    } catch (error) {
      const backendErrorMessage = getErrorMessage(error, i18nTranslate)
      setFormError(backendErrorMessage)
      setDisableFinishOnSubmit(false)
    }
  }

  const actionOnSubmit = async (formValues, setFormError, i18nTranslate) => {
    setFormError('')
    if (id) {
      await updateSsoProfile(formValues, setFormError, i18nTranslate)
    } else {
      await createSSOProfile(formValues, setFormError, i18nTranslate)
    }
  }

  const wizardButtons = {
    next: {
      label: t('common:button.next'),
      submitForm: false
    },
    finish: id
      ? t('iam:sso_profiles.view_edit_create.save_changes')
      : t('common:create')
  }

  const validateFormData = () => {
    return new Promise((resolve) => {
      resolve()
    })
  }
  const steps = [
    {
      title: t('iam:sso_profiles.view_edit_create.select_domain'),
      childComponents: (
        <General onOptionSelect={(option) => setSelectedOption(option)} />
      ),
      description: isGlcpOidc ? (
        <Typography
          type="text"
          size="large"
          weight={400}
          testId="select_domain_description_odiv2"
        >
          <Trans
            i18nKey="iam:sso_profiles.view_edit_create.select_domain_description_odiv2"
            t={t}
          >
            <Anchor
              testId="select_sso_anchor"
              label={t('iam:domains.sso_setup_guide')}
              weight="bold"
              onClick={() => dispatchEvent(new Event('context-help'))}
            />
          </Trans>
        </Typography>
      ) : (
        <Typography type="text" size="large" weight={400}>
          <Trans
            i18nKey="iam:sso_profiles.view_edit_create.select_domain_description"
            t={t}
          >
            <Anchor
              testId="select_domain_learn_anchor"
              label={t('common:learn_more')}
              weight="bold"
              onClick={() => dispatchEvent(new Event('context-help'))}
            />
          </Trans>
        </Typography>
      ),
      validateForm: (formValues) => {
        return validateForm(formValues, t, validateGeneralFields)
      }
    },
    ...(selectedOption === 'OIDC' && isGlcpOidc
      ? [
          {
            title: t('iam:sso_profiles_oidc.oidc_config_step_title'),
            childComponents: (
              <SamlOIDCConfiguration
                onIssuerUrlChange={(url) => setIssuerUrl(url)}
                profileId={id}
              />
            ),
            description: (
              <Typography
                type="text"
                size="large"
                weight={400}
                testId="oidc_config_description"
              >
                <Trans
                  i18nKey="iam:sso_profiles_oidc.oidc_config_step_description"
                  t={t}
                >
                  <Anchor
                    testId="select_domain_learn_anchor"
                    label={t('iam:sso_profiles_oidc.sso_setup_guide')}
                    weight="bold"
                    onClick={() => dispatchEvent(new Event('context-help'))}
                  />
                </Trans>
              </Typography>
            ),
            validateForm: (formValues) => {
              return validateForm(formValues, t, validateOIDCMetadata)
            }
          }
        ]
      : [
          {
            title: t('iam:sso_profiles.view_edit_create.map_saml_attributes'),
            description: (
              <Typography type="text" size="large" weight={400}>
                <Trans
                  i18nKey="iam:sso_profiles.view_edit_create.map_saml_attributes_description"
                  t={t}
                >
                  <Anchor
                    testId="map_saml_attributes_learn_anchor"
                    label={t('common:learn_more')}
                    weight="bold"
                    onClick={() => dispatchEvent(new Event('context-help'))}
                  />
                </Trans>
              </Typography>
            ),
            childComponents: <ConfigureSettings configValues={configValues} />,
            validateForm: (formValues) => {
              return validateForm(
                formValues,
                t,
                validateConfigureSettingsFields
              )
            }
          }
        ]),
    ...(selectedOption === 'OIDC' && isGlcpOidc
      ? [
          {
            title: t('iam:sso_profiles_oidc.login_client_title'),
            childComponents: <LoginClientSetUp oidcIssuerUrl={issuerUrl} />,
            description: (
              <Typography
                type="text"
                size="large"
                weight={400}
                testId="oidc_login_client_description"
              >
                <Trans
                  i18nKey="iam:sso_profiles_oidc.login_client_step_description"
                  t={t}
                >
                  <Anchor
                    testId="select_domain_learn_anchor"
                    label={t('iam:sso_profiles_oidc.sso_setup_guide')}
                    weight="bold"
                    onClick={() => dispatchEvent(new Event('context-help'))}
                  />
                </Trans>
              </Typography>
            ),
            validateForm: async (formValues, setFormError) => {
              try {
                // Validate clientId and clientSecret
                const isClientFieldsValid = validateClientFields(
                  formValues,
                  setFormError
                )
                if (!isClientFieldsValid) {
                  throw new Error('Client fields validation failed')
                }
              } catch (error) {
                console.error('Client fields validation failed:', error)
                throw new Error(
                  t(
                    'Validation of the client fields failed. Both client id and client secret are required fields.'
                  )
                )
              }
              try {
                // Call validateOidcClientId and ensure it completes successfully
                await validateOidcClientId(formValues, setFormError, t)
                return true // Proceed if validation succeeds
              } catch (error) {
                console.error('OIDC Client ID validation failed:', error)
                throw new Error(
                  t(
                    'iam:sso_profiles_oidc.login_client_already_in_use_error_msg'
                  )
                )
              }
            }
          }
        ]
      : [
          {
            title: t('iam:sso_profiles.view_edit_create.configure_idp'),
            description: (
              <Typography type="text" size="large" weight={400}>
                <Trans
                  i18nKey="iam:sso_profiles.view_edit_create.configure_idp_description"
                  t={t}
                >
                  <Anchor
                    testId="configure_idp_learn_anchor"
                    label={t('common:learn_more')}
                    weight="bold"
                    onClick={() => dispatchEvent(new Event('context-help'))}
                  />
                </Trans>
              </Typography>
            ),
            childComponents: <ConfigureIdp />,
            validateForm: () => validateFormData()
          }
        ]),
    ...(selectedOption === 'OIDC' && isGlcpOidc
      ? [
          {
            title: t(
              'iam:sso_profiles.view_edit_create.create_recovery_account'
            ),
            description: (
              <Typography type="text" size="large" weight={400}>
                <Trans
                  i18nKey="iam:sso_profiles.view_edit_create.recovery_account_description"
                  t={t}
                >
                  <Anchor
                    testId="recovery_account_learn_anchor"
                    label={t('common:learn_more')}
                    weight="bold"
                    onClick={() => dispatchEvent(new Event('context-help'))}
                  />
                </Trans>
              </Typography>
            ),
            childComponents: <RecoveryAccount />,
            validateForm: (formValues) => {
              return validateForm(formValues, t, validateRecoveryUser)
            }
          }
        ]
      : []),
    ...(selectedOption === 'OIDC' && isGlcpOidc
      ? [
          {
            title: t(
              'iam:sso_profiles.view_edit_create.specify_session_timeout'
            ),
            description: (
              <Typography type="text" size="large" weight={400}>
                <Trans
                  i18nKey="iam:sso_profiles.view_edit_create.specify_session_timeout_description"
                  t={t}
                >
                  <Anchor
                    testId="session_timeout_learn_anchor"
                    label={t('common:learn_more')}
                    weight="bold"
                    onClick={() => dispatchEvent(new Event('context-help'))}
                  />
                </Trans>
              </Typography>
            ),
            childComponents: <Timeout />,
            validateForm: (formValues) => {
              return validateForm(formValues, t, validateTimeoutField)
            }
          }
        ]
      : [
          {
            title: t(
              'iam:sso_profiles.view_edit_create.identity_provider_details'
            ),
            description: (
              <Typography type="text" size="large" weight={400}>
                <Trans
                  i18nKey="iam:sso_profiles.view_edit_create.identity_provider_details_description"
                  t={t}
                >
                  <Anchor
                    testId="identity_provider_details_learn_anchor"
                    label={t('common:learn_more')}
                    weight="bold"
                    onClick={() => dispatchEvent(new Event('context-help'))}
                  />
                </Trans>
              </Typography>
            ),
            childComponents: (
              <AddIDPDetails
                multiDomainErrorInfo={multiDomainErrorInfo}
                setMultiDomainErrorInfo={setMultiDomainErrorInfo}
              />
            ),
            validateForm: (formValues) => validateIDPDetails(formValues, t)
          }
        ]),
    ...(!(selectedOption === 'OIDC' && isGlcpOidc)
      ? [
          {
            title: t(
              'iam:sso_profiles.view_edit_create.create_recovery_account'
            ),
            description: (
              <Typography type="text" size="large" weight={400}>
                <Trans
                  i18nKey="iam:sso_profiles.view_edit_create.recovery_account_description"
                  t={t}
                >
                  <Anchor
                    testId="recovery_account_learn_anchor"
                    label={t('common:learn_more')}
                    weight="bold"
                    onClick={() => dispatchEvent(new Event('context-help'))}
                  />
                </Trans>
              </Typography>
            ),
            childComponents: <RecoveryAccount />,
            validateForm: (formValues) => {
              return validateForm(formValues, t, validateRecoveryUser)
            }
          }
        ]
      : []),
    ...(!(selectedOption === 'OIDC' && isGlcpOidc)
      ? [
          {
            title: t(
              'iam:sso_profiles.view_edit_create.specify_session_timeout'
            ),
            description: (
              <Typography type="text" size="large" weight={400}>
                <Trans
                  i18nKey="iam:sso_profiles.view_edit_create.specify_session_timeout_description"
                  t={t}
                >
                  <Anchor
                    testId="session_timeout_learn_anchor"
                    label={t('common:learn_more')}
                    weight="bold"
                    onClick={() => dispatchEvent(new Event('context-help'))}
                  />
                </Trans>
              </Typography>
            ),
            childComponents: <Timeout />,
            validateForm: (formValues) => {
              return validateForm(formValues, t, validateTimeoutField)
            }
          }
        ]
      : []),
    {
      title: id
        ? t('iam:sso_profiles.view_edit_create.review_save_changes')
        : t('iam:sso_profiles.view_edit_create.review_create'),
      description: t('iam:sso_profiles.view_edit_create.review_description'),
      childComponents: <ReviewAndCreate />,
      validateForm: () => validateFormData()
    }
  ]

  useEffect(() => {
    if (id) {
      setProfileLoading(true)
      setRoutingLoading(true)

      get(`/identity/v1alpha1/sso-profiles/${id}`, {}).then(
        (ssoResponse) => {
          setProfileData(ssoResponse?.data)
          setProfileLoading(false)
        },
        (error) => {
          setProfileLoading(false)
          setApiErrorNotification(
            displayApiError(error, t, setApiErrorNotification)
          )
        }
      )
      get(`/internal-identity/v1alpha1/sso-routing-rules`, {
        'sso-profile-id': id
      }).then(
        (res) => {
          if (res?.data?.items?.length) {
            setRoutingData(res?.data?.items[0])
          }
          setRoutingLoading(false)
        },
        (error) => {
          setRoutingLoading(false)
          setApiErrorNotification(
            displayApiError(error, t, setApiErrorNotification)
          )
        }
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, t])

  const getValueFromArrayObj = (key, arr = []) => {
    return arr.find((obj) => obj.name === key)?.expression
  }

  const getMspValues = (values) => {
    let appliesToText = ''
    if (
      values.includes(ssoModeEnum.AUTHORIZATION_MSP_MANAGED_TENANT_ONLY) ||
      values.includes(ssoModeEnum.AUTHENTICATION_ONLY_MSP_MANAGED_TENANT_ONLY)
    ) {
      appliesToText = mspOptions.custOrg
    } else if (
      values?.includes(ssoModeEnum.AUTHORIZATION_MSP_MANAGED) ||
      values?.includes(ssoModeEnum.AUTHENTICATION_ONLY_MSP_MANAGED)
    ) {
      appliesToText = [mspOptions.mspOrg, mspOptions.custOrg]
    } else {
      appliesToText = mspOptions.mspOrg
    }
    return appliesToText
  }

  const formDefaultValues = () => {
    const authorizationMode = routingData?.actions?.idp?.ssoMode

    const authnOptionsValue = () => {
      if (profileData?.oidcIdpConfig) {
        return Authnoptions.OIDC
      }
      return Authnoptions.SAML
    }

    const oidcAuthorizationModeValue = () => {
      if (isGlcpOidc && authorizationMode) {
        return authorizationMode?.includes(ssoModeEnum.AUTHENTICATION_ONLY)
          ? oidcauthorization.LOCAL
          : oidcauthorization.SSO
      }
      return undefined
    }

    const authnEditvalue = () => {
      let value = ''
      if (profileData?.samlIdpConfig) {
        value = t('iam:sso_profiles.saml')
      } else if (profileData?.oidcIdpConfig) {
        value = t('iam:sso_profiles.openID_connect')
      }
      return value
    }

    const mspMethodsValue = () => {
      if (authorizationMode) {
        return getMspValues(authorizationMode)
      }
      return undefined
    }

    const initialData = {
      general: id
        ? {
            profileName: profileData?.name,
            msp_methods: authorizationMode && [authorizationMode],
            Authnoptions: authnOptionsValue(),
            oidauthorizationMethod: oidcAuthorizationModeValue(),
            currentOidcAuthorizationMethod: oidcAuthorizationModeValue(),
            authorizationMethod:
              !isGlcpOidc &&
              (getValueFromArrayObj(
                'HPECCSAttribute',
                profileData?.attributeMapping
              )
                ? ssoModeEnum.AUTHORIZATION
                : ssoModeEnum.AUTHENTICATION_ONLY),
            currentAuthorizationMethod:
              !isGlcpOidc &&
              (getValueFromArrayObj(
                'HPECCSAttribute',
                profileData?.attributeMapping
              )
                ? ssoModeEnum.AUTHORIZATION
                : ssoModeEnum.AUTHENTICATION_ONLY)
          }
        : defaultGeneral(isGlcpOidc).general,
      msp_methods: mspMethodsValue(),
      glcpOidcFlag: isGlcpOidc,
      domain: routingData?.conditions?.domains[0],
      authnEditMode: authnEditvalue(),
      currentDomain: routingData?.conditions?.domains[0],
      attribute_mapping: id
        ? {
            email:
              getValueFromArrayObj('Email', profileData?.attributeMapping) ??
              '',
            firstName:
              getValueFromArrayObj(
                'FirstName',
                profileData?.attributeMapping
              ) ?? '',
            lastName:
              getValueFromArrayObj('LastName', profileData?.attributeMapping) ??
              '',
            ccsAttribute:
              getValueFromArrayObj(
                'HPECCSAttribute',
                profileData?.attributeMapping
              ) ??
              attributeMappingDefaultFormValues.attribute_mapping.ccsAttribute
          }
        : attributeMappingDefaultFormValues.attribute_mapping,
      timeout:
        getValueFromArrayObj(
          'IdleSessionTimeout',
          profileData?.attributeMapping
        ) || 30,
      saml_idp_config: {
        entity_id: profileData?.samlIdpConfig?.entityId,
        login_url: profileData?.samlIdpConfig?.loginUrl,
        logout_url: profileData?.samlIdpConfig?.logoutUrl,
        signing_certificate: profileData?.samlIdpConfig?.certificate,
        metadata_url: '',
        file_info: {},
        config_option: id ? 'Manual entry of configuration details' : ''
      },
      recovery_user: {
        currentPocEmail: profileData?.recoveryUser?.recoveryEmail,
        pocEmail: profileData?.recoveryUser?.recoveryEmail,
        recoveryEmail: profileData?.recoveryUser?.username,
        recoveryUserEnabled: true,
        skipPasswordValidation: !!id
      },
      oidcIdpConfig: {
        clientId: profileData?.oidcIdpConfig?.clientId,
        clientSecret: profileData?.oidcIdpConfig?.clientSecret,
        issuer: profileData?.oidcIdpConfig?.issuer,
        authorizationEndpoint:
          profileData?.oidcIdpConfig?.authorizationEndpoint,
        tokenEndpoint: profileData?.oidcIdpConfig?.tokenEndpoint,
        jwksEndpoint: profileData?.oidcIdpConfig?.jwksEndpoint,
        userInfoEndpoint: profileData?.oidcIdpConfig?.userInfoEndpoint
      }
    }
    setInitialFormData(initialData)
    return initialData
  }

  return (
    <>
      {id && (routingLoading || profileLoading) ? (
        <Box direction="row" align="center" justify="center" pad="medium">
          <Loader testId="saml-view-loader" />
        </Box>
      ) : (
        <Box>
          <Wizard
            testId="add-sso-profile-wizard"
            title={
              id
                ? t('iam:sso_profiles.view_edit_create.edit_profile')
                : t('iam:sso_profiles.create_sso_profile')
            }
            formDefaultValues={formDefaultValues}
            actionOnSubmit={(formValues, setFormError) => {
              actionOnSubmit(formValues, setFormError, t)
            }}
            disableFinishOnSubmit={disableFinishOnSubmit}
            buttonLabels={wizardButtons}
            actionOnExit={() => actionOnExitFn()}
            cancelStrings={{
              continueLabel: t(
                'iam:sso_profiles.view_edit_create.stay_in_wizard'
              ),
              exitLabel: t('iam:sso_profiles.view_edit_create.leave_wizard'),
              heading: t(
                'iam:sso_profiles.view_edit_create.leave_without_finishing'
              ),
              text: t(
                'iam:sso_profiles.view_edit_create.lose_progress_warning',
                {
                  action: id
                    ? t('iam:sso_profiles.view_edit_create.update')
                    : t('common:create')
                }
              )
            }}
            steps={steps}
          />
          {errorMsg && (
            <Notification
              backgroundColor="status-critical"
              icon={<StatusCritical color="text-strong" />}
              onClose={() => setErrorMsg(null)}
              text={errorMsg}
              testId="api-notification"
              position="top"
            />
          )}
        </Box>
      )}
      {apiErrorNotification}
    </>
  )
}

export default AddSsoProfile
